import React, { useState } from 'react';
import { Square, Circle, Triangle, Palette } from 'lucide-react';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import ColorPicker from './ColorPicker';

const SHAPE_TYPES = [
  { id: 'rect', name: 'Rektangel', icon: Square },
  { id: 'circle', name: 'Cirkel', icon: Circle },
  { id: 'triangle', name: 'Triangel', icon: Triangle },
];

const COLOR_PRESETS = [
  { color: '#000000', name: 'Svart' },
  { color: '#FFFFFF', name: 'Vit' },
  { color: '#FF0000', name: 'Röd' },
  { color: '#0066FF', name: 'Blå' },
  { color: '#00CC00', name: 'Grön' },
  { color: '#FFD700', name: 'Guld' },
  { color: '#FF6600', name: 'Orange' },
  { color: '#9900FF', name: 'Lila' },
  { color: '#00CCFF', name: 'Cyan' },
  { color: '#FF1493', name: 'Rosa' },
  { color: '#FFE500', name: 'Gul' },
  { color: '#C0C0C0', name: 'Silver' },
  { color: '#1A1A1A', name: 'Kolgrå' },
  { color: '#CC0000', name: 'Mörkröd' },
  { color: '#00008B', name: 'Mörkblå' },
];

const ShapeDrawer = ({
  activeShapeTool,
  onShapeToolSelect,
  shapeProperties,
  onShapePropertyChange,
  selectedGraphic
}) => {
  const handleFillColorChange = (color) => {
    onShapePropertyChange({ fill: color });
  };

  const isShapeSelected = selectedGraphic && selectedGraphic.type === 'shape';

  return (
    <div className="space-y-4">
      {/* Shape Tool Selection */}
      <div>
        <Label className="text-sm font-medium mb-3 block">Välj form</Label>
        <div className="grid grid-cols-3 gap-2">
          <TooltipProvider>
            {SHAPE_TYPES.map((shape) => {
              const Icon = shape.icon;
              return (
                <Tooltip key={shape.id}>
                  <TooltipTrigger asChild>
                    <Button
                      variant={activeShapeTool === shape.id ? 'default' : 'outline'}
                      size="icon"
                      className={cn(
                        'h-12 w-full',
                        activeShapeTool === shape.id && 'ring-2 ring-primary ring-offset-2'
                      )}
                      onClick={() => onShapeToolSelect(shape.id)}
                    >
                      <Icon className="h-5 w-5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{shape.name}</p>
                  </TooltipContent>
                </Tooltip>
              );
            })}
          </TooltipProvider>
        </div>
        {activeShapeTool && (
          <p className="text-xs text-muted-foreground mt-2">
            Klicka och dra på bilförhandsvisningen för att rita
          </p>
        )}
      </div>

      <Separator />

      {/* Shape Properties */}
      <div className="space-y-4">
        <div>
          <Label className="text-sm font-medium mb-3 block flex items-center gap-2">
            <Palette className="h-4 w-4" />
            Fyllnadsfärg
          </Label>
          <div className="grid grid-cols-5 gap-2">
            <TooltipProvider>
              {COLOR_PRESETS.map(({ color, name }) => (
                <Tooltip key={color}>
                  <TooltipTrigger asChild>
                    <button
                      className={cn(
                        'w-9 h-9 rounded-full transition-all border border-black/20',
                        shapeProperties.fill === color
                          ? 'ring-2 ring-primary ring-offset-2'
                          : 'hover:scale-110'
                      )}
                      style={{ backgroundColor: color }}
                      onClick={() => handleFillColorChange(color)}
                    />
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{name}</p>
                  </TooltipContent>
                </Tooltip>
              ))}
              <ColorPicker color={shapeProperties.fill} onChange={handleFillColorChange} />
            </TooltipProvider>
          </div>
        </div>
      </div>

      {isShapeSelected && (
        <div className="p-3 bg-muted rounded-lg">
          <p className="text-xs text-muted-foreground">
            Form vald. Justera egenskaper ovan eller använd handtagen för att ändra storlek.
          </p>
        </div>
      )}
    </div>
  );
};

export default ShapeDrawer;
